import {
  Box,
  Pagination,
  PaginationItem,
  Skeleton,
  Theme,
  useMediaQuery,
} from "@mui/material";
import {
  columnHeaderSx,
  emptyStateSx,
  paginationBarSx,
  paginationSx,
  permissionPillSx,
  resourceLabelSx,
  resourceListSx,
  resourceRowSx,
  roleCardChevronSx,
  roleCardGroupLabelSx,
  roleCardGroupSx,
  roleCardHeadSx,
  roleCardNameSx,
  roleCardPillSx,
  roleCardPillsSx,
  roleCardSx,
  roleNameButtonSx,
  roleNameSx,
  rolesCardSx,
  rowSx,
} from "screens/Settings/RoleManagement/Roles.styles";
import {
  PagerNextIcon,
  PagerPrevIcon,
} from "screens/Settings/RoleManagement/components/RoleIcons";
import { NavChevronIcon } from "screens/Settings/components/SettingsNavIcons";
import EmptyState from "global/components/EmptyState/EmptyState";

export interface RoleResource {
  name: string;
  path?: string;
  permissions?: string[];
}

export interface RoleRow {
  id?: number;
  name: string;
  resources?: RoleResource[];
}

interface RolesTableProps {
  roles: RoleRow[];
  loading: boolean;
  rowsPerPage: number;
  page: number;
  count: number;
  /** False for the built-in roles, which render as plain (non-clickable) text. */
  isEditable: (role: RoleRow) => boolean;
  onEdit: (role: RoleRow) => void;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
}

/**
 * Roles list exactly as specified by the Settings design: a two-column grid
 * (220px role name / permissions) inside a single rounded card, closed by a
 * right-aligned pager. All geometry comes from `Roles.tokens.ts`.
 */
const RolesTable = ({
  roles,
  loading,
  rowsPerPage,
  page,
  count,
  isEditable,
  onEdit,
  onPageChange,
}: RolesTableProps) => {
  const pageCount = Math.ceil((count || 0) / rowsPerPage);
  // Both small references replace the two-column grid with one card per role.
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const getRow = (role: RoleRow, index: number) => {
    const resources = role.resources ?? [];
    const stacked = resources.length > 1;
    const isLast = index === roles.length - 1;
    const editable = isEditable(role);

    return (
      <Box
        role="row"
        key={`${role.id ?? role.name}-${index}`}
        sx={rowSx(stacked, isLast)}
      >
        <Box role="cell" sx={{ minWidth: 0 }}>
          {editable ? (
            <Box
              component="button"
              type="button"
              onClick={() => onEdit(role)}
              aria-label={`Edit role ${role.name}`}
              sx={roleNameButtonSx(stacked)}
            >
              {role.name}
            </Box>
          ) : (
            <Box component="span" sx={roleNameSx(stacked)}>
              {role.name}
            </Box>
          )}
        </Box>

        <Box role="cell" sx={resourceListSx}>
          {resources.map((resource, resourceIndex) => (
            <Box key={`${resource.name}-${resourceIndex}`} sx={resourceRowSx}>
              <Box component="span" sx={resourceLabelSx}>
                {resource.name}
              </Box>
              {(resource.permissions ?? []).map((permission, permIndex) => (
                <Box
                  key={`${permission}-${permIndex}`}
                  component="span"
                  sx={permissionPillSx}
                >
                  {permission}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const getCard = (role: RoleRow, index: number) => {
    const resources = role.resources ?? [];
    const editable = isEditable(role);
    return (
      <Box
        key={`${role.id ?? role.name}-${index}`}
        {...(editable
          ? {
              component: "button" as const,
              type: "button" as const,
              onClick: () => onEdit(role),
              "aria-label": `Edit role ${role.name}`,
            }
          : {})}
        sx={roleCardSx}
      >
        <Box sx={roleCardHeadSx}>
          <Box component="span" sx={roleCardNameSx}>
            {role.name}
          </Box>
          {editable && <NavChevronIcon sx={roleCardChevronSx} />}
        </Box>

        {resources.map((resource, resourceIndex) => (
          <Box key={`${resource.name}-${resourceIndex}`} sx={roleCardGroupSx}>
            <Box component="span" sx={roleCardGroupLabelSx}>
              {resource.name}
            </Box>
            <Box sx={roleCardPillsSx}>
              {(resource.permissions ?? []).map((permission, permIndex) => (
                <Box
                  key={`${permission}-${permIndex}`}
                  component="span"
                  sx={roleCardPillSx}
                >
                  {permission}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const getLoadingCards = () =>
    [...Array(3)].map((_, index) => (
      <Box key={index} sx={roleCardSx} aria-hidden="true">
        <Skeleton variant="text" width="45%" height={18} />
        <Skeleton variant="text" width="80%" height={16} />
      </Box>
    ));

  const getLoadingRows = () =>
    [...Array(rowsPerPage)].map((_, index) => (
      <Box role="row" key={index} sx={rowSx(false, index === rowsPerPage - 1)}>
        <Skeleton role="cell" variant="text" width="55%" height={18} />
        <Skeleton role="cell" variant="text" width="70%" height={18} />
      </Box>
    ));

  return (
    <Box sx={rolesCardSx}>
      {isCompact ? (
        <Box role="list" aria-label="Roles" aria-busy={loading}>
          {loading ? (
            getLoadingCards()
          ) : roles.length > 0 ? (
            roles.map((role, index) => (
              <Box
                role="listitem"
                key={`${role.id ?? role.name}-${index}`}
                sx={{ display: "contents" }}
              >
                {getCard(role, index)}
              </Box>
            ))
          ) : (
            <EmptyState
              size="medium"
              icon="shield"
              accent="violet"
              title="No roles yet"
              body="Roles group the permissions you grant to your team. Create one to control what members can see and do."
            />
          )}
        </Box>
      ) : (
        <Box role="table" aria-label="Roles" aria-busy={loading}>
          <Box role="row" sx={columnHeaderSx}>
            <Box role="columnheader" component="span">
              ROLE NAME
            </Box>
            <Box role="columnheader" component="span">
              RESOURCES AND PERMISSIONS
            </Box>
          </Box>

          {loading ? (
            getLoadingRows()
          ) : roles.length > 0 ? (
            roles.map(getRow)
          ) : (
            <Box role="row">
              <Box role="cell">
                <EmptyState
                  icon="shield"
                  accent="violet"
                  title="No roles yet"
                  body="Roles group the permissions you grant to your team. Create one to control what members can see and do."
                />
              </Box>
            </Box>
          )}
        </Box>
      )}

      {!loading && pageCount > 1 && (
        <Box sx={paginationBarSx}>
          <Pagination
            page={page}
            count={pageCount}
            siblingCount={1}
            boundaryCount={1}
            shape="rounded"
            onChange={onPageChange}
            sx={paginationSx}
            renderItem={(item) => (
              <PaginationItem
                slots={{ previous: PagerPrevIcon, next: PagerNextIcon }}
                {...item}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default RolesTable;
